<template>
  <div class="tw-relative tw-group">
    <SfDragHandle
      v-if="showDragHandle"
      :dragging="dragging"
      compact
      data-testid="insight-metric-pill-drag-handle"
      class="tw-absolute tw-top-2 tw-right-2 tw-z-10"
    />
    <SfMetricPill
      v-model:expanded="expanded"
      variant="default"
      :graph="graph"
      :graph-data="graphData"
      :value="displayValue"
      :delta="displayDelta"
      :pinned="false"
      :highlighted="false"
      :read-only="readOnly"
      :hide-collapse="hideCollapse"
      :hide-x-axis-labels="!store.fullscreen"
      :compact-y-axis="!store.fullscreen"
      :loading="loading"
      :errored="errored"
      :load-error-text="I18n.t('report.drawer.pinned.load_error')"
      :highlight-disabled="store.locked || !store.canHighlight"
      :highlight-disabled-tooltip="highlightDisabledTooltip"
      :pin-disabled="store.locked"
      :pin-disabled-tooltip="lockedTooltip"
      :pin-label="I18n.t('report.drawer.pill_actions.pin')"
      :unpin-label="I18n.t('report.drawer.pill_actions.unpin')"
      :highlight-label="I18n.t('report.drawer.pill_actions.highlight')"
      :unhighlight-label="I18n.t('report.drawer.pill_actions.unhighlight')"
      @update:highlighted="onHighlightToggle"
      @update:pinned="onPinToggle"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import SfDragHandle from '@app/shared/vue/components/sf-drag-handle/sf-drag-handle.vue'
import SfMetricPill from '@app/shared/vue/components/sf-metric-pill/sf-metric-pill.vue'
import { useDrawerPillBindings } from '@app/application/report/composables/use-drawer-pill-bindings'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import type { DrawerGraphEntry } from '@app/application/report/types'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'InsightMetricPill',
})

interface Props {
  entry: DrawerGraphEntry
  readOnly?: boolean
  dragging?: boolean
  hideCollapse?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  dragging: false,
  hideCollapse: false,
})

// `defineModel` so the parent section can lift expanded pills above the grid:
// the section sets `:expanded="true"` on lifted pills (rendered full-width),
// and `:expanded="false"` on the grid pills.
const expanded = defineModel<boolean>('expanded', { default: false })

const I18n = useI18n()
const store = useReportGraphsDrawer()

// Drag handle shows on the slim variant only — the lifted-above (expanded)
// pill takes the full row and isn't draggable.
const showDragHandle = computed(
  () => !props.readOnly && !store.locked && !expanded.value
)

const lockedTooltip = computed(() => I18n.t('report.drawer.locked_tooltip'))
const highlightDisabledTooltip = computed(() =>
  store.locked
    ? lockedTooltip.value
    : I18n.t('report.drawer.highlighted.cap_reached_tooltip')
)

const {
  graph,
  graphData,
  loading,
  errored,
  displayValue,
  displayDelta,
  fetchIfNeeded,
  onHighlightToggle,
  onPinToggle,
} = useDrawerPillBindings(() => props.entry)

// `immediate: true` because lifted-above pills mount with expanded already true
// and need to fetch their chart data on mount.
watch(
  expanded,
  (open) => {
    if (open) fetchIfNeeded()
  },
  { immediate: true }
)
</script>
